import React, { useEffect, useState } from "react";
import { ActivityIndicator } from "react-native";
import LBox from "@components/LBox";
import LText from "@components/LText";
import { useMusicSyncGetPlaylistSyncProgress } from "@hooks/trpc/useMusicSync";
import { useTheme } from "@theme";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated";

type SyncStatus = "hidden" | "importing" | "completed" | "failed";

export default function PlaylistSyncProgress() {
  const data = useMusicSyncGetPlaylistSyncProgress();
  const theme = useTheme();
  const progressSharedValue = useSharedValue(0);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [trackedPlatform, setTrackedPlatform] = useState<string | undefined>();

  const progressPercentage = data
    ? data.totalPlaylistCount > 0
      ? Math.round(
          (data.processedPlaylistCount / data.totalPlaylistCount) * 100,
        )
      : 0
    : 0;

  const importedPlaylistCount = data?.importedPlaylistCount;
  const totalPlaylistCount = data?.totalPlaylistCount;

  const isInProgress =
    !!data && (data.status === "pending" || data.status === "running");
  const isSuccessfulTerminal =
    data?.status === "completed" || data?.status === "completed_with_failures";
  const isFailedTerminal =
    data?.status === "failed" || data?.status === "stale";

  // Track which platform's import we're following. Set during render (the
  // "adjust state during render" pattern) when an import is in progress, so
  // we can later recognise its completion. Cleared on dismiss.
  if (isInProgress && data && trackedPlatform !== data.platform) {
    setTrackedPlatform(data.platform);
  }

  // `status` is fully derived from the current data, the tracked platform and
  // whether the success banner is showing - so we compute it during render
  // instead of mirroring it into state via an effect.
  const status: SyncStatus = showSuccessBanner
    ? isFailedTerminal
      ? "failed"
      : "completed"
    : !data
      ? "hidden"
      : isInProgress
        ? "importing"
        : isSuccessfulTerminal && data.platform === trackedPlatform
          ? "completed"
          : isFailedTerminal && data.platform === trackedPlatform
            ? "failed"
            : "hidden";

  useEffect(() => {
    progressSharedValue.value = withTiming(progressPercentage, {
      duration: 500,
    });
  }, [progressPercentage, progressSharedValue]);

  useEffect(() => {
    // Only schedule the banner when a tracked import actually completes - not
    // while the banner is already showing (status stays "completed" then).
    if ((status === "completed" || status === "failed") && !showSuccessBanner) {
      const showBannerTimeout = setTimeout(() => {
        setShowSuccessBanner(true);
      }, 500);

      return () => {
        clearTimeout(showBannerTimeout);
      };
    }
  }, [status, showSuccessBanner]);

  const dismissSuccessBanner = () => {
    setShowSuccessBanner(false);
    // Clearing the tracked platform makes the derived `status` fall through to
    // "hidden" for the just-completed import.
    setTrackedPlatform(undefined);
  };

  const animatedProgressStyle = useAnimatedStyle(() => {
    return {
      width: `${progressSharedValue.value}%`,
    };
  });

  if (!data || status === "hidden") {
    return null;
  }

  if (showSuccessBanner) {
    const hasFailures = (data.failedPlaylistCount ?? 0) > 0;
    const importFailed = data.status === "failed" || data.status === "stale";
    return (
      <LBox
        mt="s4"
        borderRadius="m"
        borderWidth={1}
        borderColor="playlistImportCompleteBorder"
        backgroundColor="playlistImportCompleteBg"
        py="s3"
        px="s4"
        gap="s4"
        flexDirection="row"
        alignItems="center"
      >
        <LIcon name="import_complete" size={32} />
        <LBox gap="s1" flex={1}>
          <LText variant="bodyM2m" color="playlistImportCompleteTitle">
            {importFailed ? "Import needs attention" : "Import Complete"}
          </LText>
          <LText variant="bodyM" color="playlistImportCompleteSubtitle">
            {importFailed
              ? "Please reconnect your streaming service and try again"
              : hasFailures
                ? `${importedPlaylistCount} imported, ${data.failedPlaylistCount} failed`
                : `${totalPlaylistCount} Playlists added to your Library`}
          </LText>
        </LBox>
        <LPressable
          sentryEventName="dismiss_import_complete"
          onPress={dismissSuccessBanner}
        >
          <LIcon name="cross" size={16} />
        </LPressable>
      </LBox>
    );
  }

  return (
    <LBox
      mt="s4"
      borderRadius="m"
      borderWidth={1}
      borderColor="whiteTranslucent10"
      backgroundColor="softNight"
      padding="s4"
      gap="s4"
    >
      <LBox
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <LBox flexDirection="row" alignItems="center" gap="s2">
          <ActivityIndicator size="small" color={theme.colors.white} />
          <LText variant="bodyM2m" color="white">
            Importing your Playlists
          </LText>
        </LBox>
        <LText variant="bodyM" color="whiteTranslucent60">
          {progressPercentage}%
        </LText>
      </LBox>

      <LBox
        height={8}
        backgroundColor="greyTranslucent40"
        borderRadius="max"
        overflow="hidden"
      >
        <Animated.View
          style={[
            {
              height: "100%",
              borderRadius: theme.borderRadii.max,
            },
            animatedProgressStyle,
          ]}
        >
          <LBox
            height="100%"
            width="100%"
            backgroundColor={[
              "buttonBackgroundPrimaryLeft",
              "buttonBackgroundPrimaryRight",
            ]}
            gradientDirection="horizontal"
            borderRadius="max"
          />
        </Animated.View>
      </LBox>

      <LBox>
        <LText variant="bodyM" color="whiteTranslucent60">
          {totalPlaylistCount === -1 ? (
            "Processing..."
          ) : (
            <>
              {data.processedPlaylistCount}/{totalPlaylistCount} Playlists
              Processed
            </>
          )}
        </LText>
      </LBox>
    </LBox>
  );
}
